import { loadEncryptedJsonCache, saveEncryptedJsonCache } from "./storage";

const BASE_PENDING_INBOX_NOTES_KEY_PREFIX =
  "stealthos_base_pending_inbox_notes";
const ENCRYPTED_BASE_PENDING_INBOX_NOTES_KEY_PREFIX =
  "stealthos_base_pending_inbox_notes_encrypted_v1";

export type PendingInboxNote = {
  recipientKey: string;
  commitment: string;
  encryptedNote: {
    ephemeralPubKey: string;
    ciphertext: string;
    iv: string;
    authTag: string;
  };
  treeIndex?: number;
  txHash?: string;
  createdAt: number;
  attempts: number;
  lastError?: string;
};

export function pendingInboxNotesKey(address: string): string {
  return `${BASE_PENDING_INBOX_NOTES_KEY_PREFIX}:${address.trim().toLowerCase()}`;
}

export function encryptedPendingInboxNotesKey(address: string): string {
  return `${ENCRYPTED_BASE_PENDING_INBOX_NOTES_KEY_PREFIX}:${address.trim().toLowerCase()}`;
}

function normalizePendingInboxNotes(value: unknown): PendingInboxNote[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item) =>
      item &&
      typeof item.recipientKey === "string" &&
      typeof item.commitment === "string" &&
      item.encryptedNote &&
      typeof item.encryptedNote.ephemeralPubKey === "string" &&
      typeof item.encryptedNote.ciphertext === "string" &&
      typeof item.encryptedNote.iv === "string" &&
      typeof item.encryptedNote.authTag === "string" &&
      (item.treeIndex === undefined ||
        (Number.isSafeInteger(item.treeIndex) &&
          item.treeIndex >= 0 &&
          item.treeIndex <= 0xffff_ffff)),
  ) as PendingInboxNote[];
}

function dedupePendingInboxNotes(
  notes: PendingInboxNote[],
): PendingInboxNote[] {
  const byCommitment = new Map<string, PendingInboxNote>();
  for (const note of notes) {
    byCommitment.set(note.commitment.toLowerCase(), note);
  }
  return Array.from(byCommitment.values()).slice(-50);
}

function removeLegacyPendingInboxNotes(address: string): void {
  if (typeof window === "undefined" || !address) return;
  try {
    window.localStorage.removeItem(pendingInboxNotesKey(address));
  } catch {
    // Browser storage can be unavailable in strict privacy modes.
  }
}

export function readPendingInboxNotes(address: string): PendingInboxNote[] {
  removeLegacyPendingInboxNotes(address);
  return [];
}

export function writePendingInboxNotes(
  address: string,
  _notes?: PendingInboxNote[],
): void {
  removeLegacyPendingInboxNotes(address);
}

export function upsertPendingInboxNote(
  address: string,
  _note?: PendingInboxNote,
): void {
  removeLegacyPendingInboxNotes(address);
}

export async function readEncryptedPendingInboxNotes(
  address: string,
  cacheSecret: string,
): Promise<PendingInboxNote[]> {
  if (typeof window === "undefined" || !address || !cacheSecret) {
    removeLegacyPendingInboxNotes(address);
    return [];
  }
  try {
    const legacyKey = pendingInboxNotesKey(address);
    const legacyRaw = window.localStorage.getItem(legacyKey);
    let legacy: PendingInboxNote[] = [];
    if (legacyRaw) {
      legacy = normalizePendingInboxNotes(JSON.parse(legacyRaw));
      window.localStorage.removeItem(legacyKey);
    }
    const encrypted =
      (await loadEncryptedJsonCache<PendingInboxNote[]>(
        "stealthos-base-pending-inbox-cache-v1",
        encryptedPendingInboxNotesKey(address),
        address,
        cacheSecret,
      )) ?? [];
    const next = dedupePendingInboxNotes([
      ...normalizePendingInboxNotes(encrypted),
      ...legacy,
    ]);
    if (legacy.length > 0) {
      await writeEncryptedPendingInboxNotes(address, next, cacheSecret);
    }
    return next;
  } catch {
    return [];
  }
}

export async function writeEncryptedPendingInboxNotes(
  address: string,
  notes: PendingInboxNote[],
  cacheSecret: string,
): Promise<void> {
  if (
    typeof window === "undefined" ||
    !window.crypto?.subtle ||
    !address ||
    !cacheSecret
  ) {
    removeLegacyPendingInboxNotes(address);
    throw new Error("Encrypted pending-note storage is unavailable");
  }
  removeLegacyPendingInboxNotes(address);
  const key = encryptedPendingInboxNotesKey(address);
  const expected = dedupePendingInboxNotes(notes);
  if (expected.length === 0) {
    window.localStorage.removeItem(key);
    if (window.localStorage.getItem(key) !== null) {
      throw new Error("Encrypted pending-note deletion was not durable");
    }
    return;
  }
  await saveEncryptedJsonCache(
    "stealthos-base-pending-inbox-cache-v1",
    key,
    address,
    expected,
    cacheSecret,
  );
  const verified = await loadEncryptedJsonCache<PendingInboxNote[]>(
    "stealthos-base-pending-inbox-cache-v1",
    key,
    address,
    cacheSecret,
  );
  if (
    !Array.isArray(verified) ||
    JSON.stringify(normalizePendingInboxNotes(verified)) !==
      JSON.stringify(expected)
  ) {
    throw new Error("Encrypted pending-note write was not durable");
  }
}

export async function upsertEncryptedPendingInboxNote(
  address: string,
  note: PendingInboxNote,
  cacheSecret: string,
): Promise<void> {
  const existing = await readEncryptedPendingInboxNotes(address, cacheSecret);
  const normalizedCommitment = note.commitment.toLowerCase();
  const next = existing.filter(
    (item) => item.commitment.toLowerCase() !== normalizedCommitment,
  );
  next.push(note);
  await writeEncryptedPendingInboxNotes(address, next, cacheSecret);
}
